import { useCallback, useEffect, useRef, useState } from "react";
import type { WsMessage, SlideState, PresencePayload } from "@wedding/shared";

/**
 * Live feed for the Reji console — organizer-authenticated WebSocket to the
 * SlideshowRoom DO (`role=reji`): receives slide_state / presence /
 * viewer_count pushes; the 5s polling era is over.
 *
 * Ported from the proven guest pattern (apps/web slideshow): 45s "ping"
 * keepalive (the DO auto-replies "pong" without waking from hibernation),
 * exponential backoff + jitter reconnect, full cleanup on unmount.
 */
export function useRejiWs(slug: string, opts?: { observe?: boolean }) {
  const observe = opts?.observe ?? false;
  const [slideState, setSlideState] = useState<SlideState | null>(null);
  const [presence, setPresence] = useState<PresencePayload | null>(null);
  const [viewerCount, setViewerCount] = useState(0);
  const [connected, setConnected] = useState(false);
  // serverNow - Date.now(), refreshed on every slide_state → drift self-corrects.
  const clockOffsetRef = useRef(0);

  const wsRef = useRef<WebSocket | null>(null);
  const cancelledRef = useRef(false);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const attemptRef = useRef(0);

  const connect = useCallback(() => {
    if (cancelledRef.current) return;
    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const ws = new WebSocket(
      `${protocol}//${window.location.host}/api/organizer/w/${slug}/slideshow/ws${observe ? "?mode=observe" : ""}`,
    );
    ws.onopen = () => {
      setConnected(true);
      attemptRef.current = 0;
      pingTimerRef.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) ws.send("ping");
      }, 45000);
    };
    ws.onclose = () => {
      setConnected(false);
      if (pingTimerRef.current) { clearInterval(pingTimerRef.current); pingTimerRef.current = null; }
      if (!cancelledRef.current) {
        const delay = Math.min(60000, 3000 * 2 ** attemptRef.current) * (0.8 + Math.random() * 0.4);
        attemptRef.current += 1;
        reconnectTimerRef.current = setTimeout(connect, delay);
      }
    };
    ws.onmessage = (event) => {
      if (event.data === "pong") return; // keepalive ack
      let msg: WsMessage;
      try {
        msg = JSON.parse(event.data as string) as WsMessage;
      } catch {
        return;
      }
      if (msg.type === "slide_state" && msg.slideState) {
        clockOffsetRef.current = msg.slideState.serverNow - Date.now();
        setSlideState(msg.slideState);
      } else if (msg.type === "presence" && msg.presence) {
        setPresence(msg.presence);
      } else if (msg.type === "viewer_count" && msg.viewerCount !== undefined) {
        setViewerCount(msg.viewerCount);
      }
    };
    wsRef.current = ws;
  }, [slug, observe]);

  useEffect(() => {
    cancelledRef.current = false;
    connect();
    return () => {
      cancelledRef.current = true;
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      if (pingTimerRef.current) clearInterval(pingTimerRef.current);
      wsRef.current?.close();
    };
  }, [connect]);

  // Graceful close on real page exits → the Reji badge / counts drop
  // instantly; bfcache restores reconnect on pageshow.
  useEffect(() => {
    const onPageHide = () => {
      cancelledRef.current = true;
      wsRef.current?.close();
    };
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) {
        cancelledRef.current = false;
        connect();
      }
    };
    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("pageshow", onPageShow);
    return () => {
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("pageshow", onPageShow);
    };
  }, [connect]);

  return { slideState, presence, viewerCount, connected, clockOffsetRef };
}
